import React, { useCallback, useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Video } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

import CreateAdvertisingsButton from "./CreateAdvertisingsButton";
import { advertisingColumns } from "./tableParams/advertisingColumns";
import OfferAPI from "../../api/OfferAPI";
import ItemStatus from "../../utils/ItemStatus";
import CappedPage from "../common/CappedPage";
import DataTable from "../dataTable/DataTable";
import DataTableEmptyState from "../dataTable/DataTableEmptyState";
import { advertisingPath } from "../routes/routes";
import Breadcrumbs from "../utils/Breadcrumbs";

import type { JSX } from "react";

import type { IGetAdvertisingResponse } from "../../types";
import type { AdvertisingRow } from "./tableParams/advertisingColumns.types";

function AdvertisingsList(): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t } = useTranslation("editor-advertisings");
  const queryClient = useQueryClient();
  const history = useHistory();

  const getAdvertisingListQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getAdvertisingList,
    params: [{ projectSlug }],
  });

  const {
    data: advertisingsData,
    isLoading,
    isError,
    refetch,
  } = useQuery(getAdvertisingListQuery);

  const advertisingRowList = useMemo(
    (): AdvertisingRow[] =>
      (advertisingsData?.data ?? []).map((advertising) => ({
        ...advertising,
        status: ItemStatus.getAdvertisingStatus(advertising),
      })),
    [advertisingsData?.data],
  );

  const { mutate: deleteAdvertising } = useMutation({
    mutationFn: async (uuid: string): Promise<unknown> =>
      OfferAPI.deleteAdvertising({ uuid, projectSlug }),
    onMutate: async (uuid) => {
      await queryClient.cancelQueries(getAdvertisingListQuery.queryKey);

      const previousData = queryClient.getQueryData<IGetAdvertisingResponse>(
        getAdvertisingListQuery.queryKey,
      );

      queryClient.setQueryData<IGetAdvertisingResponse>(
        getAdvertisingListQuery.queryKey,
        (old) =>
          old && {
            ...old,
            data: old.data.filter((advertising) => advertising.uuid !== uuid),
          },
      );

      return { previousData };
    },
    onSuccess: () => {
      toast.success(t("deleteCampaignSuccess"));
    },
    onError: (_error, _uuid, context) => {
      queryClient.setQueryData(
        getAdvertisingListQuery.queryKey,
        context?.previousData,
      );
      toast.error(t("deleteCampaignError"));
    },
    onSettled: async () =>
      queryClient.invalidateQueries(getAdvertisingListQuery.queryKey),
  });

  const getRowHref = useCallback(
    (advertising: AdvertisingRow): string =>
      advertisingPath(projectSlug, advertising.uuid),
    [projectSlug],
  );

  const columnList = useMemo(
    () =>
      advertisingColumns({
        t,
        projectSlug,
        onEdit: (advertising) => history.push(getRowHref(advertising)),
        onDelete: (advertising) => deleteAdvertising(advertising.uuid),
      }),
    [t, projectSlug, history, getRowHref, deleteAdvertising],
  );

  const breadcrumbList = [
    {
      name: t("sectionTitle"),
    },
  ];

  const emptyState = (
    <DataTableEmptyState
      action={<CreateAdvertisingsButton />}
      icon={Video}
      message={t("createCampaignText")}
      title={t("sectionTitle")}
    />
  );

  return (
    <CappedPage contentClassName="pt-7.5 pb-15">
      <Breadcrumbs items={breadcrumbList} />
      <DataTable
        columns={columnList}
        data={advertisingRowList}
        defaultSorting={{ id: "updated_at", desc: true }}
        emptyState={emptyState}
        getRowHref={getRowHref}
        headerActions={<CreateAdvertisingsButton />}
        isError={isError}
        isLoading={isLoading}
        onRetry={refetch}
        title={t("sectionTitle")}
      />
    </CappedPage>
  );
}

export default AdvertisingsList;
